import { connect } from 'react-redux';
import { push } from 'redux-first-history';
import queryString from 'query-string';
import EditRentAmountPage from './component';
import { getIreneDashboard, requestRentAmountChange } from '@rental-manager/actions/requests';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import type { ErrorInterface } from '@common/types';
import { trackValidationError } from '@common/actions/TrackingActions';
import {
  DEFAULT_ERROR,
  RENT_AMOUNT_UPDATE_FAIL,
  RENT_AMOUNT_UPDATE_SUCCESS,
} from '@rental-manager/constants/NotificationConstants';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import HTTP_STATUS from '@common/constants/HttpConstants';
import { formatYMDStringToTimestamp } from '@common/paymentsUtils/dateUtils';
import FeaturePermissionTypes from '@rental-manager/constants/FeaturePermissionTypes';
import { logError } from '@zg-rentals/log-error';

const mapStateToProps = (state, ownProps) => {
  const rentTermsKeyFromQueryString = queryString.parse(ownProps.location.search).rentTermsKey;
  const { currentTerm, nextTerm, featurePermissions, userConfig, rentalProtectionDisclaimers, listingAlias } =
    state.payments.landlord.property;

  let rentTerm;
  if (rentTermsKeyFromQueryString && nextTerm?.rentTermsKey === rentTermsKeyFromQueryString) {
    rentTerm = nextTerm;
  } else if (rentTermsKeyFromQueryString && currentTerm?.rentTermsKey === rentTermsKeyFromQueryString) {
    rentTerm = currentTerm;
  }

  const { tenantFirstName, nextPayable, amount, rentAmountChangeRequest, endDateForPayments, rentTermsKey } = rentTerm;

  const { isRentGuaranteeEnabled } = userConfig;
  const rentGuarantee = featurePermissions?.[rentTermsKey]?.[FeaturePermissionTypes.RENT_GUARANTEE].exhausted;

  return {
    tenantFirstName,
    nextPayable,
    amount,
    rentAmountChangeRequest,
    endDateForPayments,
    rentTermsKey,
    listingAlias,
    isRentalProtectionVariation: isRentGuaranteeEnabled && rentGuarantee,
    rentalProtectionDisclaimers,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId }) => ({
  onSubmit: async ({ rentAmount, effectiveDate, rentTermsKey }) => {
    try {
      // Close any open banners
      await dispatch(closeNotification());

      // Request to create a rent amount change
      const rentAmountChangeReponse = await dispatch(
        requestRentAmountChange({
          propertyId,
          rentTermsKey,
          newAmount: rentAmount,
          amountChangeEffectiveDueDate: formatYMDStringToTimestamp(effectiveDate),
        }),
      );
      await checkRequestActionSuccess(rentAmountChangeReponse);

      return { success: true, effectiveDate };
    } catch (err) {
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status } = error.action.payload;

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }
      }

      logError({ error: err });
      await dispatch(showModalNotification(RENT_AMOUNT_UPDATE_FAIL));
      return { error: true };
    }
  },
  continueToDashboard: async ({ tenantFirstName }) => {
    try {
      // Close any open banners
      await dispatch(closeNotification());

      // Request the latest landlord status
      const [landlordStatusResponse] = await dispatch(getIreneDashboard({ propertyId }));
      await checkRequestActionSuccess(landlordStatusResponse);

      // Navigate to dashboard
      await dispatch(push(baseUrl));

      // Show success banner
      await dispatch(showPageNotification(RENT_AMOUNT_UPDATE_SUCCESS, { tenantFirstName }));

      return { error: false };
    } catch (err) {
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status } = error.action.payload;

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }
      }

      logError({ error: err });
      await dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  onClose: async () => {
    try {
      // Close any open banners
      await dispatch(closeNotification());

      // Request the latest landlord status
      const [landlordStatusResponse] = await dispatch(getIreneDashboard({ propertyId }));
      await checkRequestActionSuccess(landlordStatusResponse);

      // Navigate to dashboard
      await dispatch(push(baseUrl));
    } catch (err) {
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status } = error.action.payload;

        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }
      }

      logError({ error: err });
      await dispatch(showModalNotification(DEFAULT_ERROR));
      return { error: true };
    }
  },
  trackValidationError: (data) => dispatch(trackValidationError(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditRentAmountPage);
